import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Refresh, ErrorOutline } from "@material-ui/icons";
import { axiosInstance } from "../../Utils/ExpressAPI";

/**
 * Indicateurs du parcours (sections 2, 30 et 31 du cahier des charges).
 *
 * Regle centrale de cet ecran : quand la mesure n'existe pas, on ecrit qu'elle
 * n'existe pas. Aucun zero de complaisance, aucun taux calcule sur zero
 * dossier. Un indicateur faux est pire que pas d'indicateur, parce qu'il
 * fabrique une confiance que rien ne justifie.
 */

const PERIODES = [
  { cle: "jour", libelle: "Aujourd'hui", heures: 24 },
  { cle: "semaine", libelle: "7 jours", heures: 24 * 7 },
  { cle: "mois", libelle: "30 jours", heures: 24 * 30 },
];

const useStyles = makeStyles(() => ({
  page: { padding: 24, maxWidth: 1100, margin: "0 auto" },
  entete: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  aide: { color: "#6B7280", fontSize: 13, marginBottom: 20, maxWidth: 700 },
  periodes: { display: "flex", gap: 8, marginBottom: 20 },
  grille: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
    gap: 16,
  },
  carte: { padding: 20 },
  valeur: { fontSize: 30, fontWeight: 600, color: "#111827", lineHeight: 1.1 },
  valeurAbsente: { fontSize: 16, fontWeight: 500, color: "#9CA3AF", lineHeight: 1.6 },
  etiquette: { fontSize: 13, color: "#374151", marginTop: 6, fontWeight: 500 },
  precision: { fontSize: 12, color: "#6B7280", marginTop: 6 },
  erreur: {
    padding: 24,
    display: "flex",
    alignItems: "center",
    gap: 12,
    color: "#991B1B",
  },
}));

const pourcentage = (v) => (v == null ? null : `${Math.round(v * 100)} %`);
const secondes = (v) => {
  if (v == null) return null;
  if (v < 60) return `${v} s`;
  return `${Math.floor(v / 60)} min ${String(v % 60).padStart(2, "0")}`;
};

export default function Indicateurs() {
  const classes = useStyles();
  const labId = localStorage.getItem("currentLab");

  const [periode, setPeriode] = useState("jour");
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    const heures = PERIODES.find((p) => p.cle === periode).heures;
    const depuis = new Date(Date.now() - heures * 3600 * 1000).toISOString();
    try {
      const { data } = await axiosInstance.get("dossiers/indicateurs", {
        params: { laboratoryId: labId, depuis },
      });
      setDonnees(data);
    } catch (e) {
      setErreur(
        e?.response?.data?.error ||
          "Les indicateurs n'ont pas pu être chargés. Le service est peut-être encore en cours de déploiement."
      );
    } finally {
      setChargement(false);
    }
  }, [labId, periode]);

  useEffect(() => {
    charger();
  }, [charger]);

  const cartes = donnees
    ? [
        {
          valeur: String(donnees.dossiers),
          etiquette: "Dossiers traités",
          precision: "Sur la période choisie.",
        },
        {
          valeur: secondes(donnees.tempsMoyenSecondes),
          etiquette: "Temps moyen par dossier",
          precision:
            "L'objectif du cahier des charges est de rester sous 90 secondes pour un dossier standard.",
        },
        {
          valeur: secondes(donnees.tempsMedianSecondes),
          etiquette: "Temps médian",
          precision:
            "La médiane décrit le dossier courant ; la moyenne se laisse tirer par quelques cas longs.",
        },
        {
          valeur: donnees.dossiers ? String(donnees.dossiersHors90s) : null,
          etiquette: "Dossiers au-delà de 90 s",
          precision: "Ceux qui dépassent l'objectif.",
        },
        {
          valeur: pourcentage(donnees.tauxReconnaissanceAutomatique),
          etiquette: "Analyses reconnues automatiquement",
          precision: "Part des analyses associées à un code sans intervention humaine.",
        },
        {
          valeur: pourcentage(donnees.tauxCorrection),
          etiquette: "Analyses corrigées à la main",
          precision:
            "Mesure la qualité réelle du moteur : c'est ce chiffre qui doit baisser avec le temps.",
        },
        {
          valeur: pourcentage(donnees.tauxException),
          etiquette: "Dossiers nécessitant une intervention",
          precision: "Part des dossiers qui remontent dans la file du secrétariat.",
        },
      ]
    : [];

  return (
    <div className={classes.page}>
      <div className={classes.entete}>
        <Typography variant="h5">Indicateurs</Typography>
        <Button
          startIcon={<Refresh />}
          onClick={charger}
          style={{ textTransform: "none" }}
        >
          Rafraîchir
        </Button>
      </div>
      <div className={classes.aide}>
        Ces chiffres sont calculés sur les dossiers réellement enregistrés. Quand
        une mesure n'est pas disponible, elle est affichée comme telle plutôt que
        remplacée par un zéro.
      </div>

      <div className={classes.periodes}>
        {PERIODES.map((p) => (
          <Button
            key={p.cle}
            size="small"
            variant={periode === p.cle ? "contained" : "outlined"}
            color={periode === p.cle ? "primary" : "default"}
            style={{ textTransform: "none" }}
            onClick={() => setPeriode(p.cle)}
          >
            {p.libelle}
          </Button>
        ))}
      </div>

      {chargement && (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress />
        </Box>
      )}

      {!chargement && erreur && (
        <Paper className={classes.erreur}>
          <ErrorOutline />
          <span>{erreur}</span>
        </Paper>
      )}

      {!chargement && !erreur && donnees && (
        <div className={classes.grille}>
          {cartes.map((c) => (
            <Paper key={c.etiquette} className={classes.carte}>
              {c.valeur ? (
                <div className={classes.valeur}>{c.valeur}</div>
              ) : (
                <div className={classes.valeurAbsente}>Pas encore mesuré</div>
              )}
              <div className={classes.etiquette}>{c.etiquette}</div>
              <div className={classes.precision}>{c.precision}</div>
            </Paper>
          ))}
        </div>
      )}
    </div>
  );
}
